import SearchIcon from "@mui/icons-material/Search";
import { FormHelperText, IconButton, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../../constants";
import CustomAutocomplete from "../../../../shared/AutoComplete";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../../shared/CustomTextFeild";
import { setParticipantDetails } from "../../../../store/features/auth/fundParticipantsSlice";
import { setCreateNewInvoice } from "../../../../store/ProjectInvoices/newProjectInvoiceSlice";
import { RootState } from "../../../../store/store";
import CancelPopUp from "../../../PopUp/PopUp";
import VendorListPopUp from "../../../PopUp/VendorListPopUp";
import { getAllParticipants } from "../../services/ParticipantsServices/SwitchParticipartService";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer
} from "../../StyledComponents/Container";

const NewInvoice = () => {
  const dispatch = useDispatch();
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const participantInfo = useSelector((state: RootState) => state.participantDetails);
  const newInvoice = useSelector((state: RootState) => state.newInvoice);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const navigate = useNavigate();
  const [openPopUp, setOpenPopUp] = useState(false);
  const [openCancelPopUp, setOpenCancelPopUp] = useState(false);
  const [siteName, setSiteName] = useState<string>(newInvoice.siteName || "");
  const [vendor, setVendor] = useState(newInvoice.vendor || "");
  const [projectNumber, setProjectNumber] = useState(newInvoice.projectNumber || "");
  const [participantId, setParticipantId] = useState<any>(newInvoice?.participantId); // or any default valid ID
  const UserInfo = useSelector((state: RootState) => state.auth);


  const [errors, setErrors] = useState({
    siteName: "",
    vendor: "",
    projectNumber: "",
  });

  useEffect(() => {
    if (contactInformation.participantAdminProfile !== constants.ADMIN_VIEW) {
      setParticipantId(participantInfo?.participantId)
      setSiteName(contactInformation.participantAdminProfile);
    }
  }, [contactInformation.participantAdminProfile]);

  const handlePopUpClose = () => {
    setOpenPopUp(false);
  };
  const handleCancelPopUpClose = () => {
    setOpenCancelPopUp(false);
  };
  useEffect(() => {
    getParticipants();
  }, [])
  async function getParticipants() {
    let participantVO = {
      clientId: UserInfo.clientId,
      page: -1,
      size: -1,
      isActive: "Y",
    };
    try {
      const promise = await getAllParticipants(participantVO);
      dispatch(setParticipantDetails({ participantsArray: promise.data?.data?.data }));
    } catch (error) {
      console.error(error);
    }
  }
  const validateFields = () => {
    let isValid = true;
    const newErrors = { siteName: "", vendor: "", projectNumber: "" };

    if (!siteName || (typeof siteName === "string" && siteName.trim() === "")) {
      newErrors.siteName = translate("pleaseSelectSiteName");
      isValid = false;
    }

    if (!vendor?.trim()) {
      newErrors.vendor = translate("pleaseSelectVendor");
      isValid = false;
    }
    if (!projectNumber?.trim()) {
      newErrors.projectNumber = translate("pleaseSelectProjectNumber");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleContinue = () => {
    if (validateFields()) {
      dispatch(setCreateNewInvoice({
        siteName,
        vendor,
        projectNumber,
        participantId
      }));
      navigate("/enterInvoiceDetails");
    }
  };
  const handleCancel = () => {
    setOpenCancelPopUp(true);
  }
  const handleSearchClick = () => {
    setOpenPopUp(true);
  };

  const handleVendorSelect = (vendorName: string) => {
    setVendor(vendorName);
    setErrors((prevErrors) => ({ ...prevErrors, vendor: "" }));
  };
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchClick();
    }
  };
  const handleConfirm = () => {
    navigate("/vendorInvoiceSummary");
  };
  return (
    <StyledParentContainer>
      <StyledOneChildContainer sx={{ border: "none" }}>
        <StyledTwoChildContainer>
          <SectionTitle>{translate("enterSiteandVendorDetails")}</SectionTitle>
          <Box sx={{ width: "100%", px: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
                <Box sx={{ mt: 2, ml: 2 }}>
                  {contactInformation.participantAdminProfile === constants.ADMIN_VIEW ? (
                    <><CustomAutocomplete
                      sx={{ width: "50%", mb: 2 }}
                      labelText={translate("selectSiteName")}
                      required
                      placeholder={translate("selectSiteName")}
                      options={participantInfo?.participantsArray}
                      getOptionLabel={(option) => option.participantName ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option.participantName === value.participantName
                      }
                      value={
                        participantInfo?.participantsArray.find(
                          (p: any) => p.participantName === siteName
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setSiteName(newValue?.participantName ?? "");
                        setParticipantId(newValue?.participantId ?? 0);
                        setErrors((prevErrors) => ({ ...prevErrors, siteName: "" }));
                      }}
                      error={!!errors.siteName}
                    />
                      {errors.siteName && (
                        <FormHelperText error>
                          {errors.siteName}
                        </FormHelperText>
                      )}
                    </>
                  ) : (
                    <>
                      <CustomTextField
                        labelText={translate("selectSiteName")}
                        required
                        readOnly={true}
                        value={siteName}
                        sx={{ width: "50%" }}
                        error={!!errors.siteName}
                      />
                      {errors.siteName && (
                        <FormHelperText error>
                          {errors.siteName}
                        </FormHelperText>
                      )}
                    </>
                  )}
                  <>
                    <CustomTextField
                      labelText={translate("selectVendor")}
                      placeholder={translate("selectVendor")}
                      required
                      value={vendor}
                      error={!!errors.vendor}
                      onKeyDown={handleKeyPress}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleSearchClick}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: "50%" }}
                    />
                    {errors.vendor && (
                      <FormHelperText sx={{ ml: 0, mt: -2 }} error>
                        {errors.vendor}
                      </FormHelperText>
                    )}
                  </>

                  <CustomTextField
                    labelText={translate("projectNumber")}
                    placeholder={translate("projectNumber")}
                    value={projectNumber}
                    required
                    error={!!errors.projectNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProjectNumber(value); // Always update the input
                      if (value.trim()) {
                        setErrors((prevErrors) => ({ ...prevErrors, projectNumber: "" }));
                      }
                    }}
                    onBlur={() => {
                      setProjectNumber((prev) => prev.trim());
                    }}

                    inputProps={{ maxLength: 45 }}
                    sx={{ width: "50%" }}
                  />
                  {errors.projectNumber && (
                    <FormHelperText sx={{ mt: -2 }} error>
                      {errors.projectNumber}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ marginBottom: 2 }} className="right-fields">
              <ContainedButton onClick={handleContinue}>
                {translate("continue")}
              </ContainedButton>
              <OutlinedButton onClick={handleCancel} sx={{ ml: 1 }}>
                {translate("cancel")}
              </OutlinedButton>
            </Box>
          </Box>
          <CancelPopUp
            open={openCancelPopUp}
            popupTitle={translate("confirmCancel")}
            handleClose={handleCancelPopUpClose}
            saveChanges={handleConfirm}
            message={translate("doYouWantToCancelChanges?")}
          />
          <VendorListPopUp
            open={openPopUp}
            popupTitle={translate("selectVendor")}
            handleClose={handlePopUpClose}
            onVendorSelect={handleVendorSelect}
            participantId={participantId}
          />
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default NewInvoice;
